// Object-shaped Proxy that delegates property reads/writes onto a store slice.
// Function values are held locally (the store stays a data store; callbacks
// don't need to be observable).
//
//   const params = createStateProxy<ParamsState>(store, 'params');
//   params.sliceZ = 3.5;          // -> store.set('params.sliceZ', 3.5)
//   params.sliceZ                 // <- store.get('params.sliceZ')
//   params.resetView = () => ...; // kept on the proxy, not in store

import type { Store } from './store';

/**
 * The proxy exposes the slice as a plain mutable object — that's what
 * the pane's `addBinding(params, 'sliceOpacity', …)` expects. Anything
 * else that's stored locally (functions) layers on top via intersection.
 */
export function createStateProxy<TSlice extends object>(store: Store<object>, slicePath: string): TSlice {
  const localFns: Record<PropertyKey, unknown> = Object.create(null);
  const target: Record<PropertyKey, unknown> = Object.create(null);

  return new Proxy(target, {
    get(_t, key) {
      if (key in localFns) return localFns[key];
      return store.get(`${slicePath}.${String(key)}`);
    },
    set(_t, key, value) {
      if (typeof value === 'function') {
        localFns[key] = value;
      } else {
        store.set(`${slicePath}.${String(key)}`, value);
      }
      return true;
    },
    has(_t, key) {
      if (key in localFns) return true;
      const slice = store.get(slicePath);
      return !!(slice && typeof slice === 'object' && Object.hasOwn(slice, key));
    },
    ownKeys(_t) {
      const slice = (store.get(slicePath) ?? {}) as Record<string, unknown>;
      return Array.from(new Set([
        ...Object.keys(slice),
        ...Object.keys(localFns),
      ]));
    },
    getOwnPropertyDescriptor(_t, key) {
      if (key in localFns) {
        return { enumerable: true, configurable: true, writable: true, value: localFns[key] };
      }
      const slice = store.get(slicePath);
      if (slice && typeof slice === 'object' && Object.hasOwn(slice, key)) {
        return {
          enumerable: true,
          configurable: true,
          writable: true,
          value: (slice as Record<string, unknown>)[key as string],
        };
      }
      return undefined;
    },
  }) as TSlice;
}
